import { api } from '@goaltree/backend/convex/_generated/api'
import type { Doc } from '@goaltree/backend/convex/_generated/dataModel'
import { coachFace, defaultRealisticFor, realisticCoach } from '@goaltree/backend/convex/lib/coach'
import { cn } from '@goaltree/ui/lib/utils'
import { useMutation, useQuery } from 'convex/react'
import { useTranslation } from 'react-i18next'

import { IllustratedCoachAvatar, RealisticCoachAvatar } from '@/components/coach-avatar'
import { useIsPro } from '@/hooks/use-is-pro'
import { optimisticSetCoachLook } from '@/lib/prefs-optimistic'

type Prefs = Partial<Doc<'userPreferences'>>

// Illustrated / Realistic coach look. Switching on realistic is Pro. A coach
// already saved as realistic stays selected, and illustrated is free.
export function CoachLookToggle({
  onLocked,
  className,
}: {
  onLocked: () => void
  className?: string
}) {
  const { t } = useTranslation()
  const { isPro } = useIsPro()
  const prefs = useQuery(api.preferences.getMyPreferences) as Prefs | undefined
  const setCoachLook = useMutation(api.preferences.setCoachLook).withOptimisticUpdate(
    optimisticSetCoachLook,
  )
  const coachVoice = prefs?.coachVoice
  const look = prefs?.coachAvatarStyle === 'realistic' ? 'realistic' : 'illustrated'

  return (
    <div
      className={cn(
        'flex shrink-0 items-center gap-1 rounded-lg border bg-muted/50 p-1',
        className,
      )}
    >
      {(['illustrated', 'realistic'] as const).map((style) => {
        const active = look === style
        return (
          <button
            key={style}
            type="button"
            aria-pressed={active}
            onClick={() => {
              if (active) return
              if (style === 'realistic' && !isPro) {
                onLocked()
                return
              }
              void setCoachLook({ style })
            }}
            className={cn(
              'flex flex-1 cursor-pointer items-center justify-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium transition-all',
              active
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {style === 'realistic' ? (
              <RealisticCoachAvatar
                image={
                  (coachFace(coachVoice, 'realistic') ??
                    coachFace(defaultRealisticFor(coachVoice), 'realistic'))!.image
                }
                size={18}
              />
            ) : (
              <IllustratedCoachAvatar
                voice={
                  realisticCoach(coachVoice)
                    ? (prefs?.coachIllustratedVoice ?? undefined)
                    : coachVoice
                }
                size={18}
              />
            )}
            {t(`coach.avatar.${style}`)}
          </button>
        )
      })}
    </div>
  )
}
